/**
 * Branded-search budget model: what a weekly paid branded-search budget buys
 * in total branded clicks (paid + organic), and the spend level where the
 * cost of the next click meets a target. One pure function, called by the
 * Budget Recommender page's panel and by the API route behind the MCP's
 * `branded_search_budget` tool. Fetching stays with each caller; everything
 * from "here are the daily rows" to "here is the ladder" lives here.
 * BUDGET_RECOMMENDER.md ("Branded-search budget") documents the method.
 *
 * Unit: the company's BRANDED_SEARCH-typed Google campaigns — campaign-level
 * spend, clicks and Google conversion value — plus Search Console branded
 * organic clicks and impressions and GA organic sessions and revenue, all at
 * day grain, bucketed here into the company's reporting weeks.
 *
 * Yield is the branded click-through rate: (paid clicks + organic branded
 * clicks) ÷ Search Console branded impressions. Impressions stand in for
 * demand, which spend does not move; raw click counts do move with demand
 * (high-spend weeks are launch and BFCM weeks), so the curve is fitted on
 * CTR and clicks are re-expressed at current demand.
 */
import { daysSinceWeekStart, type WeekStartDay } from '../kpi/week';
import { CUBE_CAMPAIGNS, CUBE_ROW_LIMIT } from './compute';
import { BRAND_SEARCH_INITIATIVE_TYPE } from './types';

/** Complete weeks fetched: the lookback year plus the year before it. */
export const BRANDED_SEARCH_FETCH_WEEKS = 104;
/** Weeks the tier table and value-per-session read; the curve weights these 1.0. */
export const BRANDED_SEARCH_LOOKBACK_WEEKS = 52;
/** Fewer fit weeks than this and the model reports insufficient history. */
export const BRANDED_SEARCH_MIN_FIT_WEEKS = 26;
/** Trailing complete weeks that define "current" spend and demand. */
export const BRANDED_SEARCH_DEMAND_WEEKS = 4;
/** Recent weeks shown against the same weeks a year earlier. */
export const BRANDED_SEARCH_YOY_WEEKS = 4;
/** Spend levels on the ladder between the lowest observed spend and the cap. */
export const BRANDED_SEARCH_LADDER_LEVELS = 8;
/** The ladder and the recommendation never exceed this × the highest observed weekly spend. */
export const BRANDED_SEARCH_MAX_EXTRAPOLATION = 1.25;
/** Weight of weeks older than the lookback year in the curve fit. */
export const BRANDED_SEARCH_PRIOR_WEIGHT = 0.5;
/** GA organic sessions below this × the lookback median mark a sessions outage week. */
export const BRANDED_SEARCH_SESSIONS_OUTAGE_RATIO = 0.4;
/** Paid revenue above this × the lookback median marks a revenue outlier week. */
export const BRANDED_SEARCH_REVENUE_OUTLIER_RATIO = 10;
/** Spend tiers in the "what budget levels bought" table. */
export const BRANDED_SEARCH_TIERS = 5;

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;
const YEAR_WEEKS = 52;

/** One day of merged inputs. Zero where a source has nothing for the day. */
export type BrandedSearchDailyRow = {
  /** YYYY-MM-DD */
  date: string;
  /** BRANDED_SEARCH-typed Google campaigns */
  paidSpend: number;
  paidClicks: number;
  paidImpressions: number;
  /** Google conversion value on those campaigns (account conversion window) */
  paidRevenue: number;
  /** Search Console, queries containing a brand term */
  organicClicks: number;
  organicImpressions: number;
  /** GA organic search, all queries */
  organicSessions: number;
  organicRevenue: number;
};

export type BrandedSearchWeekFlag =
  /** No Search Console branded impressions: no yield can be read. */
  | 'no-search-console'
  /** GA organic sessions far below the lookback median: left out of value per session. */
  | 'sessions-outage'
  /** Paid revenue far above the lookback median: left out of value per session. */
  | 'revenue-outlier';

export type BrandedSearchWeek = {
  /** YYYY-MM-DD of the company's week start day */
  weekStart: string;
  /** YYYY-MM-DD, weekStart + 6 */
  weekEnd: string;
  /** Distinct dates with any row in the week */
  days: number;
  paidSpend: number;
  paidClicks: number;
  paidImpressions: number;
  paidRevenue: number;
  organicClicks: number;
  organicImpressions: number;
  organicSessions: number;
  organicRevenue: number;
  totalClicks: number;
  /** totalClicks ÷ organicImpressions; null without Search Console data */
  ctr: number | null;
  /** True for the newest BRANDED_SEARCH_LOOKBACK_WEEKS weeks */
  inLookback: boolean;
  flags: BrandedSearchWeekFlag[];
};

export type BrandedSearchTier = {
  tier: number;
  weeks: number;
  /** Means over the tier's weeks */
  paidSpend: number;
  paidClicks: number;
  organicClicks: number;
  totalClicks: number;
  organicImpressions: number;
  ctr: number;
  /** Δ spend ÷ Δ total clicks against the tier below; null on the first tier or when clicks fell */
  costPerMarginalClick: number | null;
  /** Δ spend ÷ (Δ CTR × this tier's impressions): the same read with demand held constant */
  costPerMarginalClickAdjusted: number | null;
};

export type BrandedSearchLadderKind = 'organic-only' | 'level' | 'current' | 'recommended';

export type BrandedSearchLadderRow = {
  kind: BrandedSearchLadderKind;
  weeklySpend: number;
  dailyBudget: number;
  /** Predicted branded CTR at this spend */
  ctr: number;
  /** ctr × current weekly branded impressions */
  expectedClicks: number;
  /** Cost of the next click at this spend: (1 + spend) ÷ (b × impressions) */
  costPerMarginalClick: number | null;
  /** value per session ÷ costPerMarginalClick */
  marginalMer: number | null;
  /** spend ÷ clicks gained over spending nothing */
  avgCostPerIncrementalClick: number | null;
  incrementalSpendVsCurrent: number;
  incrementalClicksVsCurrent: number;
  /** Above the highest weekly spend observed in the lookback year */
  extrapolated: boolean;
};

export type BrandedSearchYoYWeek = {
  weekStart: string;
  paidSpend: number;
  totalClicks: number;
  organicImpressions: number;
  ctr: number | null;
};

export type BrandedSearchYoYRow = {
  current: BrandedSearchYoYWeek;
  lastYear: BrandedSearchYoYWeek | null;
};

export type BrandedSearchCurve = {
  /** ctr = a + b × ln(1 + weeklySpend) */
  a: number;
  b: number;
  /** Weighted R² of the fit */
  r2: number;
  /** Weeks in the fit (Search Console data present) */
  weeks: number;
  /** Observed weekly spend range across the fit weeks */
  spendMin: number;
  spendMax: number;
  /** Highest weekly spend inside the lookback year; the ladder marks spend above it */
  lookbackSpendMax: number;
  /** Predicted CTR is clamped here */
  ctrCap: number;
};

export type BrandedSearchConfidence = 'high' | 'medium' | 'low';

export type BrandedSearchStatus = 'ok' | 'insufficient-history' | 'no-measurable-yield';

export type BrandedSearchTargetSource = 'user' | 'target-mer' | 'break-even' | 'none';

export type BrandedSearchModel = {
  status: BrandedSearchStatus;
  /** Why the status is not ok, in one sentence */
  reason: string | null;
  confidence: BrandedSearchConfidence | null;
  weeks: BrandedSearchWeek[];
  curve: BrandedSearchCurve | null;
  demand: {
    /** Mean Search Console branded impressions over the trailing demand weeks */
    weeklyImpressions: number;
    weeks: number;
  };
  value: {
    /** (GA organic revenue + paid revenue) ÷ (GA organic sessions + paid clicks) */
    perSession: number | null;
    /** GA organic revenue ÷ GA organic sessions: the conservative bound */
    organicPerSession: number | null;
    /** Google conversion value ÷ paid clicks */
    paidRevenuePerClick: number | null;
    weeksUsed: number;
  };
  target: {
    costPerMarginalClick: number | null;
    source: BrandedSearchTargetSource;
    targetMer: number | null;
  };
  current: {
    weeklySpend: number;
    dailyBudget: number;
    /** Mean observed CTR over the demand weeks with Search Console data */
    observedCtr: number | null;
    /** Mean observed total clicks over the demand weeks */
    totalClicks: number;
    weeks: number;
  };
  recommendation: {
    weeklySpend: number;
    dailyBudget: number;
    ctr: number;
    expectedClicks: number;
    costPerMarginalClick: number | null;
    incrementalSpendVsCurrent: number;
    incrementalClicksVsCurrent: number;
    extrapolated: boolean;
    /** True when the target asked for more than the cap allows */
    capped: boolean;
  } | null;
  ladder: BrandedSearchLadderRow[];
  /** Lookback-year weeks bucketed by spend */
  tiers: BrandedSearchTier[];
  /** The year before, same buckets */
  priorTiers: BrandedSearchTier[];
  yoy: BrandedSearchYoYRow[];
};

export type BrandedSearchModelOptions = {
  /** Financial Settings target MER; the default target is value per session ÷ this */
  targetMer?: number | null;
  /** User override for the target cost per marginal click */
  targetCostPerMarginalClick?: number | null;
  lookbackWeeks?: number;
  minFitWeeks?: number;
  demandWeeks?: number;
  ladderLevels?: number;
  maxExtrapolation?: number;
};

// ---------------------------------------------------------------------------
// Dates and weeks

function parseDay(value: string): number {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function formatDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** The company week (YYYY-MM-DD of its first day) a date falls in. */
export function brandedSearchWeekStart(date: string, weekStartDay: WeekStartDay): string {
  const ms = parseDay(date);
  return formatDay(ms - daysSinceWeekStart(new Date(ms).getUTCDay(), weekStartDay) * DAY_MS);
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((x, y) => x - y);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? (sorted[mid] as number) : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

function mean(values: number[]): number {
  return values.length === 0 ? 0 : values.reduce((s, v) => s + v, 0) / values.length;
}

function ratio(numerator: number, denominator: number): number | null {
  return denominator > 0 && Number.isFinite(numerator) ? numerator / denominator : null;
}

function round(value: number, digits = 2): number {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}

/** Ladder spend levels read better on round numbers. */
function roundNice(spend: number): number {
  const step = spend >= 5000 ? 500 : spend >= 1000 ? 100 : spend >= 200 ? 50 : 10;
  return Math.round(spend / step) * step;
}

function emptyWeek(weekStart: string): BrandedSearchWeek {
  return {
    weekStart,
    weekEnd: formatDay(parseDay(weekStart) + 6 * DAY_MS),
    days: 0,
    paidSpend: 0,
    paidClicks: 0,
    paidImpressions: 0,
    paidRevenue: 0,
    organicClicks: 0,
    organicImpressions: 0,
    organicSessions: 0,
    organicRevenue: 0,
    totalClicks: 0,
    ctr: null,
    inLookback: false,
    flags: [],
  };
}

/**
 * Bucket merged daily rows into complete company weeks, oldest first. A week
 * is complete when its last day is on or before `lastCompleteDate` (the
 * newest day the pipeline has fully landed, n-1 on the page); the partial
 * newest week never reaches the model. Weeks with no rows at all are not
 * invented — a gap in the fetch stays a gap.
 */
export function bucketBrandedSearchWeeks(
  rows: BrandedSearchDailyRow[],
  options: {
    weekStartDay: WeekStartDay;
    lastCompleteDate: string;
    /** First day the fetch covered; a week starting before it is partial and dropped */
    firstCompleteDate?: string;
    lookbackWeeks?: number;
  },
): BrandedSearchWeek[] {
  const lookbackWeeks = options.lookbackWeeks ?? BRANDED_SEARCH_LOOKBACK_WEEKS;
  const lastComplete = parseDay(options.lastCompleteDate);
  const firstComplete = options.firstCompleteDate ? parseDay(options.firstCompleteDate) : null;
  const byWeek = new Map<string, BrandedSearchWeek & { dates: Set<string> }>();
  for (const row of rows) {
    const date = row.date.slice(0, 10);
    if (parseDay(date) > lastComplete) continue;
    const weekStart = brandedSearchWeekStart(date, options.weekStartDay);
    let week = byWeek.get(weekStart);
    if (!week) {
      week = { ...emptyWeek(weekStart), dates: new Set() };
      byWeek.set(weekStart, week);
    }
    week.dates.add(date);
    week.paidSpend += num(row.paidSpend);
    week.paidClicks += num(row.paidClicks);
    week.paidImpressions += num(row.paidImpressions);
    week.paidRevenue += num(row.paidRevenue);
    week.organicClicks += num(row.organicClicks);
    week.organicImpressions += num(row.organicImpressions);
    week.organicSessions += num(row.organicSessions);
    week.organicRevenue += num(row.organicRevenue);
  }
  const weeks = [...byWeek.values()]
    .filter((w) => parseDay(w.weekEnd) <= lastComplete && (firstComplete === null || parseDay(w.weekStart) >= firstComplete))
    .sort((x, y) => x.weekStart.localeCompare(y.weekStart))
    .map(({ dates, ...w }) => {
      const totalClicks = w.paidClicks + w.organicClicks;
      return {
        ...w,
        days: dates.size,
        totalClicks,
        ctr: w.organicImpressions > 0 ? totalClicks / w.organicImpressions : null,
      };
    });
  const lookbackFrom = weeks.length - lookbackWeeks;
  return weeks.map((w, i) => ({ ...w, inLookback: i >= lookbackFrom }));
}

// ---------------------------------------------------------------------------
// Flags

function flagWeeks(weeks: BrandedSearchWeek[]): BrandedSearchWeek[] {
  const lookback = weeks.filter((w) => w.inLookback);
  const sessionsMedian = median(lookback.map((w) => w.organicSessions).filter((v) => v > 0));
  const revenueMedian = median(lookback.map((w) => w.paidRevenue).filter((v) => v > 0));
  return weeks.map((w) => {
    const flags: BrandedSearchWeekFlag[] = [];
    if (!(w.organicImpressions > 0)) flags.push('no-search-console');
    if (sessionsMedian !== null && w.organicSessions < BRANDED_SEARCH_SESSIONS_OUTAGE_RATIO * sessionsMedian) flags.push('sessions-outage');
    if (revenueMedian !== null && w.paidRevenue > BRANDED_SEARCH_REVENUE_OUTLIER_RATIO * revenueMedian) flags.push('revenue-outlier');
    return { ...w, flags };
  });
}

// ---------------------------------------------------------------------------
// Curve

type FitPoint = { x: number; y: number; w: number };

/** Weighted least squares of y on x. */
function fitWeighted(points: FitPoint[]): { a: number; b: number; r2: number } | null {
  const sw = points.reduce((s, p) => s + p.w, 0);
  if (sw <= 0) return null;
  const mx = points.reduce((s, p) => s + p.w * p.x, 0) / sw;
  const my = points.reduce((s, p) => s + p.w * p.y, 0) / sw;
  const sxx = points.reduce((s, p) => s + p.w * (p.x - mx) ** 2, 0);
  if (sxx <= 1e-9) return null;
  const sxy = points.reduce((s, p) => s + p.w * (p.x - mx) * (p.y - my), 0);
  const b = sxy / sxx;
  const a = my - b * mx;
  const ssTot = points.reduce((s, p) => s + p.w * (p.y - my) ** 2, 0);
  const ssRes = points.reduce((s, p) => s + p.w * (p.y - (a + b * p.x)) ** 2, 0);
  return { a, b, r2: ssTot > 0 ? Math.max(0, 1 - ssRes / ssTot) : 0 };
}

export function predictBrandedCtr(curve: Pick<BrandedSearchCurve, 'a' | 'b' | 'ctrCap'>, weeklySpend: number): number {
  const raw = curve.a + curve.b * Math.log1p(Math.max(0, weeklySpend));
  return Math.min(curve.ctrCap, Math.max(0, raw));
}

/** Cost of the next click at `weeklySpend`: d(spend)/d(clicks) = (1 + spend) ÷ (b × impressions). */
export function brandedCostPerMarginalClick(
  curve: Pick<BrandedSearchCurve, 'a' | 'b' | 'ctrCap'>,
  weeklySpend: number,
  impressions: number,
): number | null {
  if (!(curve.b > 0) || !(impressions > 0)) return null;
  // Past the CTR cap the curve is flat: the next click costs infinitely much.
  if (curve.a + curve.b * Math.log1p(Math.max(0, weeklySpend)) >= curve.ctrCap) return null;
  return (1 + Math.max(0, weeklySpend)) / (curve.b * impressions);
}

/** The weekly spend at which the next click costs `target`: target × b × impressions − 1. */
export function brandedSpendForMarginalCost(
  curve: Pick<BrandedSearchCurve, 'a' | 'b'>,
  target: number,
  impressions: number,
): number | null {
  if (!(curve.b > 0) || !(impressions > 0) || !(target > 0)) return null;
  return Math.max(0, target * curve.b * impressions - 1);
}

// ---------------------------------------------------------------------------
// Tiers and year over year

export function brandedSearchTiers(weeks: BrandedSearchWeek[], tierCount = BRANDED_SEARCH_TIERS): BrandedSearchTier[] {
  const usable = weeks.filter((w) => w.ctr !== null).sort((x, y) => x.paidSpend - y.paidSpend);
  const n = usable.length;
  if (n === 0) return [];
  const count = Math.min(tierCount, n);
  const tiers: BrandedSearchTier[] = [];
  let previous: BrandedSearchTier | null = null;
  for (let i = 0; i < count; i += 1) {
    const slice = usable.slice(Math.floor((i * n) / count), Math.floor(((i + 1) * n) / count));
    if (slice.length === 0) continue;
    const tier: BrandedSearchTier = {
      tier: tiers.length + 1,
      weeks: slice.length,
      paidSpend: mean(slice.map((w) => w.paidSpend)),
      paidClicks: mean(slice.map((w) => w.paidClicks)),
      organicClicks: mean(slice.map((w) => w.organicClicks)),
      totalClicks: mean(slice.map((w) => w.totalClicks)),
      organicImpressions: mean(slice.map((w) => w.organicImpressions)),
      ctr: mean(slice.map((w) => w.ctr as number)),
      costPerMarginalClick: null,
      costPerMarginalClickAdjusted: null,
    };
    if (previous) {
      const dSpend = tier.paidSpend - previous.paidSpend;
      const dClicks = tier.totalClicks - previous.totalClicks;
      const dAdjusted = (tier.ctr - previous.ctr) * tier.organicImpressions;
      tier.costPerMarginalClick = dSpend > 0 && dClicks > 0 ? dSpend / dClicks : null;
      tier.costPerMarginalClickAdjusted = dSpend > 0 && dAdjusted > 0 ? dSpend / dAdjusted : null;
    }
    tiers.push(tier);
    previous = tier;
  }
  return tiers;
}

function yoyWeek(w: BrandedSearchWeek): BrandedSearchYoYWeek {
  return {
    weekStart: w.weekStart,
    paidSpend: w.paidSpend,
    totalClicks: w.totalClicks,
    organicImpressions: w.organicImpressions,
    ctr: w.ctr,
  };
}

export function brandedSearchYoY(weeks: BrandedSearchWeek[], recentWeeks = BRANDED_SEARCH_YOY_WEEKS): BrandedSearchYoYRow[] {
  const byStart = new Map(weeks.map((w) => [w.weekStart, w]));
  return weeks.slice(-recentWeeks).map((w) => {
    const lastYear = byStart.get(formatDay(parseDay(w.weekStart) - YEAR_WEEKS * WEEK_MS)) ?? null;
    return { current: yoyWeek(w), lastYear: lastYear ? yoyWeek(lastYear) : null };
  });
}

function roundOrNull(value: number | null): number | null {
  return value === null ? null : round(value);
}

const LADDER_KIND_PRIORITY: Record<BrandedSearchLadderKind, number> = { recommended: 3, current: 2, 'organic-only': 1, level: 0 };

/** Sort by spend; when two rows sit within 1% of each other keep the more specific kind. */
function dedupeLadder(rows: BrandedSearchLadderRow[]): BrandedSearchLadderRow[] {
  const sorted = [...rows].sort((x, y) => x.weeklySpend - y.weeklySpend || LADDER_KIND_PRIORITY[y.kind] - LADDER_KIND_PRIORITY[x.kind]);
  const out: BrandedSearchLadderRow[] = [];
  for (const row of sorted) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.weeklySpend - row.weeklySpend) <= 0.01 * Math.max(last.weeklySpend, row.weeklySpend, 1)) {
      if (LADDER_KIND_PRIORITY[row.kind] > LADDER_KIND_PRIORITY[last.kind]) out[out.length - 1] = row;
      continue;
    }
    out.push(row);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The model

export function buildBrandedSearchModel(input: BrandedSearchWeek[], options: BrandedSearchModelOptions = {}): BrandedSearchModel {
  const lookbackWeeks = options.lookbackWeeks ?? BRANDED_SEARCH_LOOKBACK_WEEKS;
  const minFitWeeks = options.minFitWeeks ?? BRANDED_SEARCH_MIN_FIT_WEEKS;
  const demandWeeks = options.demandWeeks ?? BRANDED_SEARCH_DEMAND_WEEKS;
  const ladderLevels = options.ladderLevels ?? BRANDED_SEARCH_LADDER_LEVELS;
  const maxExtrapolation = options.maxExtrapolation ?? BRANDED_SEARCH_MAX_EXTRAPOLATION;

  const ordered = [...input].sort((x, y) => x.weekStart.localeCompare(y.weekStart));
  const lookbackFrom = ordered.length - lookbackWeeks;
  const weeks = flagWeeks(ordered.map((w, i) => ({ ...w, inLookback: i >= lookbackFrom })));
  const lookback = weeks.filter((w) => w.inLookback);
  const prior = weeks.filter((w) => !w.inLookback);

  // Current spend and demand: the trailing complete weeks.
  const recent = weeks.slice(-demandWeeks);
  const recentWithSearch = recent.filter((w) => w.ctr !== null);
  const fitWeeks = weeks.filter((w) => w.ctr !== null);
  const demandImpressions =
    recentWithSearch.length > 0 ? mean(recentWithSearch.map((w) => w.organicImpressions)) : mean(fitWeeks.map((w) => w.organicImpressions));
  const currentSpend = mean(recent.map((w) => w.paidSpend));
  const current: BrandedSearchModel['current'] = {
    weeklySpend: round(currentSpend),
    dailyBudget: round(currentSpend / 7),
    observedCtr: recentWithSearch.length > 0 ? round(mean(recentWithSearch.map((w) => w.ctr as number)), 4) : null,
    totalClicks: round(mean(recent.map((w) => w.totalClicks)), 0),
    weeks: recent.length,
  };

  // Value per session over the clean lookback weeks.
  const valueWeeks = lookback.filter((w) => !w.flags.includes('sessions-outage') && !w.flags.includes('revenue-outlier'));
  const organicRevenue = valueWeeks.reduce((s, w) => s + w.organicRevenue, 0);
  const paidRevenue = valueWeeks.reduce((s, w) => s + w.paidRevenue, 0);
  const organicSessions = valueWeeks.reduce((s, w) => s + w.organicSessions, 0);
  const paidClicks = valueWeeks.reduce((s, w) => s + w.paidClicks, 0);
  const perSession = ratio(organicRevenue + paidRevenue, organicSessions + paidClicks);
  const value: BrandedSearchModel['value'] = {
    perSession: perSession === null ? null : round(perSession),
    organicPerSession: roundOrNull(ratio(organicRevenue, organicSessions)),
    paidRevenuePerClick: roundOrNull(ratio(paidRevenue, paidClicks)),
    weeksUsed: valueWeeks.length,
  };

  // Target cost per marginal click: user > target MER > break-even.
  const targetMer = options.targetMer !== undefined && options.targetMer !== null && options.targetMer > 0 ? options.targetMer : null;
  let target: BrandedSearchModel['target'];
  if (
    options.targetCostPerMarginalClick !== undefined &&
    options.targetCostPerMarginalClick !== null &&
    options.targetCostPerMarginalClick > 0
  ) {
    target = { costPerMarginalClick: options.targetCostPerMarginalClick, source: 'user', targetMer };
  } else if (perSession !== null && perSession > 0 && targetMer !== null) {
    target = { costPerMarginalClick: round(perSession / targetMer), source: 'target-mer', targetMer };
  } else if (perSession !== null && perSession > 0) {
    target = { costPerMarginalClick: round(perSession), source: 'break-even', targetMer };
  } else {
    target = { costPerMarginalClick: null, source: 'none', targetMer };
  }

  const tiers = brandedSearchTiers(lookback);
  const priorTiers = brandedSearchTiers(prior);
  const yoy = brandedSearchYoY(weeks);

  const base = {
    weeks,
    demand: { weeklyImpressions: round(demandImpressions, 0), weeks: recentWithSearch.length },
    value,
    target,
    current,
    tiers,
    priorTiers,
    yoy,
  };

  if (fitWeeks.length < minFitWeeks) {
    return {
      ...base,
      status: 'insufficient-history',
      reason: `${fitWeeks.length} complete weeks with Search Console branded data; ${minFitWeeks} are needed.`,
      confidence: null,
      curve: null,
      recommendation: null,
      ladder: [],
    };
  }

  const fit = fitWeighted(
    fitWeeks.map((w) => ({ x: Math.log1p(w.paidSpend), y: w.ctr as number, w: w.inLookback ? 1 : BRANDED_SEARCH_PRIOR_WEIGHT })),
  );
  const spendMin = Math.min(...fitWeeks.map((w) => w.paidSpend));
  const spendMax = Math.max(...fitWeeks.map((w) => w.paidSpend));
  const lookbackSpendMax = Math.max(0, ...lookback.filter((w) => w.ctr !== null).map((w) => w.paidSpend));
  if (!fit || !(fit.b > 0) || !(spendMax > 0)) {
    return {
      ...base,
      status: 'no-measurable-yield',
      reason: !(spendMax > 0)
        ? 'No paid branded-search spend in the fetched weeks, so there is no spend curve to read.'
        : 'Branded CTR does not rise with spend across the fetched weeks, so no spend level can be recommended.',
      confidence: null,
      curve: fit ? { ...fit, weeks: fitWeeks.length, spendMin, spendMax, lookbackSpendMax, ctrCap: 1 } : null,
      recommendation: null,
      ladder: [],
    };
  }

  const ctrCap = Math.max(1, 1.1 * Math.max(...fitWeeks.map((w) => w.ctr as number)));
  const curve: BrandedSearchCurve = {
    a: fit.a,
    b: fit.b,
    r2: round(fit.r2, 4),
    weeks: fitWeeks.length,
    spendMin,
    spendMax,
    lookbackSpendMax,
    ctrCap,
  };
  const confidence: BrandedSearchConfidence =
    fit.r2 >= 0.5 && fitWeeks.length >= lookbackWeeks ? 'high' : fit.r2 >= 0.25 ? 'medium' : 'low';
  const impressions = demandImpressions;
  const cap = maxExtrapolation * spendMax;
  const spendAtCap = Math.expm1((ctrCap - curve.a) / curve.b);

  const rowAt = (kind: BrandedSearchLadderKind, weeklySpend: number): BrandedSearchLadderRow => {
    const ctr = predictBrandedCtr(curve, weeklySpend);
    const ctrAtZero = predictBrandedCtr(curve, 0);
    const ctrAtCurrent = predictBrandedCtr(curve, currentSpend);
    const expectedClicks = ctr * impressions;
    const cost = brandedCostPerMarginalClick(curve, weeklySpend, impressions);
    const incrementalClicks = (ctr - ctrAtZero) * impressions;
    return {
      kind,
      weeklySpend: round(weeklySpend),
      dailyBudget: round(weeklySpend / 7),
      ctr: round(ctr, 4),
      expectedClicks: round(expectedClicks, 0),
      costPerMarginalClick: roundOrNull(cost),
      marginalMer: cost !== null && cost > 0 && perSession !== null ? round(perSession / cost) : null,
      avgCostPerIncrementalClick: weeklySpend > 0 && incrementalClicks > 0 ? round(weeklySpend / incrementalClicks) : null,
      incrementalSpendVsCurrent: round(weeklySpend - currentSpend),
      incrementalClicksVsCurrent: round((ctr - ctrAtCurrent) * impressions, 0),
      extrapolated: weeklySpend > lookbackSpendMax,
    };
  };

  let recommendation: BrandedSearchModel['recommendation'] = null;
  if (target.costPerMarginalClick !== null) {
    const wanted = brandedSpendForMarginalCost(curve, target.costPerMarginalClick, impressions) ?? 0;
    const bounded = Math.min(wanted, cap, Number.isFinite(spendAtCap) ? spendAtCap : wanted);
    const row = rowAt('recommended', Math.max(0, bounded));
    recommendation = {
      weeklySpend: row.weeklySpend,
      dailyBudget: row.dailyBudget,
      ctr: row.ctr,
      expectedClicks: row.expectedClicks,
      costPerMarginalClick: row.costPerMarginalClick,
      incrementalSpendVsCurrent: row.incrementalSpendVsCurrent,
      incrementalClicksVsCurrent: row.incrementalClicksVsCurrent,
      extrapolated: row.extrapolated,
      capped: wanted > bounded + 0.5,
    };
  }

  // Ladder: organic-only, evenly spaced levels from the lowest observed spend
  // to the cap, plus the current and recommended spend, de-duplicated.
  const positiveSpends = fitWeeks.map((w) => w.paidSpend).filter((s) => s > 0);
  const low = positiveSpends.length > 0 ? Math.min(...positiveSpends) : 0;
  const levels: BrandedSearchLadderRow[] = [rowAt('organic-only', 0)];
  for (let i = 0; i < ladderLevels; i += 1) {
    const spend = roundNice(low + ((cap - low) * (i + 1)) / ladderLevels);
    if (spend > 0) levels.push(rowAt('level', spend));
  }
  if (currentSpend > 0) levels.push(rowAt('current', currentSpend));
  if (recommendation) levels.push(rowAt('recommended', recommendation.weeklySpend));
  const ladder = dedupeLadder(levels);

  return { ...base, status: 'ok', reason: null, confidence, curve, recommendation, ladder };
}

// ---------------------------------------------------------------------------
// Splitting a weekly target across the typed campaigns

export type BrandedSearchCampaignShare = {
  campaignId: string;
  campaignName: string;
  /** Spend over the trailing demand weeks; the pro-rata key */
  trailingSpend: number;
  currentDailyBudgetCents: number | null;
};

export type BrandedSearchCampaignBudget = BrandedSearchCampaignShare & {
  share: number;
  dailyBudgetCents: number;
};

/**
 * Split a weekly spend target into per-campaign daily budgets, pro-rata on
 * trailing spend. Campaigns without trailing spend get nothing unless none
 * spent, in which case the split is even. Cents sum exactly to the target.
 */
export function splitBrandedSearchDailyBudget(weeklySpend: number, campaigns: BrandedSearchCampaignShare[]): BrandedSearchCampaignBudget[] {
  if (campaigns.length === 0) return [];
  const totalCents = Math.round((Math.max(0, weeklySpend) / 7) * 100);
  const totalTrailing = campaigns.reduce((s, c) => s + Math.max(0, c.trailingSpend), 0);
  const shares = campaigns.map((c) => (totalTrailing > 0 ? Math.max(0, c.trailingSpend) / totalTrailing : 1 / campaigns.length));
  const cents = shares.map((share) => Math.floor(totalCents * share));
  let remainder = totalCents - cents.reduce((s, c) => s + c, 0);
  const order = shares.map((share, i) => ({ share, i })).sort((x, y) => y.share - x.share);
  for (const { i } of order) {
    if (remainder <= 0) break;
    cents[i] = (cents[i] ?? 0) + 1;
    remainder -= 1;
  }
  return campaigns.map((c, i) => ({ ...c, share: round(shares[i] ?? 0, 4), dailyBudgetCents: cents[i] ?? 0 }));
}

// ---------------------------------------------------------------------------
// Cube queries and row parsing (the API fetches; the shapes live here so a
// second caller reads the same columns)

export const BRANDED_SEARCH_CHANNELS = ['google_ads', 'youtube_ads'] as const;

/** Campaign-cube measures the model reads, in the order they are requested. */
export const BRANDED_SEARCH_CAMPAIGN_MEASURES = ['spend', 'clicks', 'impressions', 'purchases'] as const;

/** `metrics` cube measures by the daily-row field they feed. */
export const BRANDED_SEARCH_METRICS_MEASURES = {
  organicClicks: 'google_search_organic_branded_search_clicks',
  organicImpressions: 'google_search_organic_branded_search_impressions',
  organicSessions: 'ga_sessions_from_organic_sessions',
  organicRevenue: 'ga_revenue_from_organic_sessions',
} as const;

export const CUBE_METRICS = 'metrics';

/**
 * Day-grain spend, clicks, impressions and Google conversion value for the
 * company's BRANDED_SEARCH-typed campaigns on the Google channels. The
 * pipeline splits every Google campaign into a youtube_ads portion and a
 * google_ads residual under one campaign id, so both channels are read and
 * summed per campaign.
 */
export function brandedSearchCampaignQuery(companyId: string, dateRange: [string, string]) {
  return {
    dimensions: [`${CUBE_CAMPAIGNS}.campaign_id`, `${CUBE_CAMPAIGNS}.campaign_name`],
    measures: BRANDED_SEARCH_CAMPAIGN_MEASURES.map((m) => `${CUBE_CAMPAIGNS}.${m}`),
    filters: [
      { member: `${CUBE_CAMPAIGNS}.companyid`, operator: 'equals' as const, values: [companyId] },
      { member: `${CUBE_CAMPAIGNS}.initiative_type`, operator: 'equals' as const, values: [BRAND_SEARCH_INITIATIVE_TYPE] },
      { member: `${CUBE_CAMPAIGNS}.media_channel`, operator: 'equals' as const, values: [...BRANDED_SEARCH_CHANNELS] },
    ],
    timeDimensions: [{ dimension: `${CUBE_CAMPAIGNS}.md_date`, granularity: 'day' as const, dateRange }],
    limit: CUBE_ROW_LIMIT,
  };
}

/** Day-grain Search Console branded clicks and impressions plus GA organic sessions and revenue. */
export function brandedSearchMetricsQuery(dateRange: [string, string]) {
  return {
    measures: Object.values(BRANDED_SEARCH_METRICS_MEASURES).map((m) => `${CUBE_METRICS}.${m}`),
    timeDimensions: [{ dimension: `${CUBE_METRICS}.date`, granularity: 'day' as const, dateRange }],
    limit: CUBE_ROW_LIMIT,
  };
}

type CubeRow = Record<string, unknown>;

function rowDay(row: CubeRow, dimension: string): string | null {
  const value = row[`${dimension}.day`] ?? row[dimension];
  return typeof value === 'string' && value.length >= 10 ? value.slice(0, 10) : null;
}

/** Merge the two cubes' day rows into one series, zero where a source has no row for the day. */
export function mergeBrandedSearchCubeRows(campaignRows: readonly CubeRow[], metricsRows: readonly CubeRow[]): BrandedSearchDailyRow[] {
  const byDate = new Map<string, BrandedSearchDailyRow>();
  const dayFor = (date: string) => {
    let row = byDate.get(date);
    if (!row) {
      row = {
        date,
        paidSpend: 0,
        paidClicks: 0,
        paidImpressions: 0,
        paidRevenue: 0,
        organicClicks: 0,
        organicImpressions: 0,
        organicSessions: 0,
        organicRevenue: 0,
      };
      byDate.set(date, row);
    }
    return row;
  };
  for (const row of campaignRows) {
    const date = rowDay(row, `${CUBE_CAMPAIGNS}.md_date`);
    if (!date) continue;
    const day = dayFor(date);
    day.paidSpend += num(row[`${CUBE_CAMPAIGNS}.spend`]);
    day.paidClicks += num(row[`${CUBE_CAMPAIGNS}.clicks`]);
    day.paidImpressions += num(row[`${CUBE_CAMPAIGNS}.impressions`]);
    day.paidRevenue += num(row[`${CUBE_CAMPAIGNS}.purchases`]);
  }
  for (const row of metricsRows) {
    const date = rowDay(row, `${CUBE_METRICS}.date`);
    if (!date) continue;
    const day = dayFor(date);
    day.organicClicks += num(row[`${CUBE_METRICS}.${BRANDED_SEARCH_METRICS_MEASURES.organicClicks}`]);
    day.organicImpressions += num(row[`${CUBE_METRICS}.${BRANDED_SEARCH_METRICS_MEASURES.organicImpressions}`]);
    day.organicSessions += num(row[`${CUBE_METRICS}.${BRANDED_SEARCH_METRICS_MEASURES.organicSessions}`]);
    day.organicRevenue += num(row[`${CUBE_METRICS}.${BRANDED_SEARCH_METRICS_MEASURES.organicRevenue}`]);
  }
  return [...byDate.values()].sort((x, y) => x.date.localeCompare(y.date));
}

/**
 * Per-campaign spend over [since, until] from the campaign-cube day rows —
 * the pro-rata key for splitting a weekly target. Every campaign seen in the
 * fetch is listed, so one that stopped spending shows up with zero.
 */
export function brandedSearchCampaignShares(campaignRows: readonly CubeRow[], since: string, until: string): BrandedSearchCampaignShare[] {
  const byCampaign = new Map<string, BrandedSearchCampaignShare & { lastSeen: string }>();
  for (const row of campaignRows) {
    const campaignId = String(row[`${CUBE_CAMPAIGNS}.campaign_id`] ?? '');
    const date = rowDay(row, `${CUBE_CAMPAIGNS}.md_date`);
    if (!campaignId || !date) continue;
    let share = byCampaign.get(campaignId);
    if (!share) {
      share = { campaignId, campaignName: '', trailingSpend: 0, currentDailyBudgetCents: null, lastSeen: '' };
      byCampaign.set(campaignId, share);
    }
    if (date >= share.lastSeen) {
      share.lastSeen = date;
      share.campaignName = String(row[`${CUBE_CAMPAIGNS}.campaign_name`] ?? share.campaignName);
    }
    if (date >= since && date <= until) share.trailingSpend += num(row[`${CUBE_CAMPAIGNS}.spend`]);
  }
  return [...byCampaign.values()]
    .map(({ lastSeen, ...share }) => ({ ...share, trailingSpend: round(share.trailingSpend) }))
    .sort((x, y) => y.trailingSpend - x.trailingSpend || x.campaignName.localeCompare(y.campaignName));
}
